import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    CardContent,
    Stack,
    Tooltip,
    TablePagination,
    Alert,
    Tabs,
    Tab,
    CircularProgress,
    TextField
} from '@mui/material';
import {
    Add as AddIcon,
    Visibility as VisibilityIcon,
    Delete as DeleteIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    PlayArrow as PlayArrowIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Schedule as ScheduleIcon,
    Send as SendIcon,
    People as PeopleIcon,
    Message as MessageIcon,
    Edit as EditIcon,
    Help as HelpIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axios from '../../utils/axios';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import BroadcastGuide from './BroadcastGuide';

export default function BroadcastsList() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [tasks, setTasks] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedTask, setSelectedTask] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [logsOpen, setLogsOpen] = useState(false);
    const [logs, setLogs] = useState([]);
    const [logsStats, setLogsStats] = useState(null);
    const [logsLoading, setLogsLoading] = useState(false);
    const [errorSummary, setErrorSummary] = useState([]);
    const [logsStatusFilter, setLogsStatusFilter] = useState('');
    const [logsSearch, setLogsSearch] = useState('');
    
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalTasks, setTotalTasks] = useState(0);
    const [guideOpen, setGuideOpen] = useState(false);

    useEffect(() => {
        loadTasks();
        loadStats();
        
        const interval = setInterval(() => {
            loadTasks();
            loadStats();
        }, 5000);

        return () => clearInterval(interval);
    }, [statusFilter, page, rowsPerPage]);

    const loadTasks = async () => {
        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage
            };
            
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }

            const response = await axios.get('/broadcasts', { params });
            setTasks(response.data.tasks);
            setTotalTasks(response.data.pagination.total);
        } catch (error) {
            console.error('Error loading tasks:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const response = await axios.get('/broadcasts/stats');
            setStats(response.data);
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    };

    const handleViewDetails = async (task) => {
        setSelectedTask(task);
        setDetailsOpen(true);
    };

    const handleViewLogs = async (task) => {
        setSelectedTask(task);
        setLogsOpen(true);
        setLogsStatusFilter('');
        setLogsSearch('');
        loadLogs(task._id);
    };

    const loadLogs = async (taskId, statusFilter = '', search = '') => {
        setLogsLoading(true);

        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (search) params.search = search;

            const [logsResponse, errorsResponse] = await Promise.all([
                axios.get(`/broadcasts/${taskId}/logs`, { params }),
                axios.get(`/broadcasts/${taskId}/errors`)
            ]);

            setLogs(logsResponse.data.logs);
            setLogsStats(logsResponse.data.stats);
            setErrorSummary(errorsResponse.data);
        } catch (error) {
            enqueueSnackbar('Ошибка загрузки логов', { variant: 'error' });
        } finally {
            setLogsLoading(false);
        }
    };

    const handleCancelTask = async (taskId) => {
        if (!window.confirm('Вы уверены, что хотите отменить эту рассылку?')) {
            return;
        }

        try {
            await axios.patch(`/broadcasts/${taskId}/cancel`);
            enqueueSnackbar('Рассылка отменена', { variant: 'success' });
            loadTasks();
            loadStats();
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || 'Ошибка отмены рассылки', { variant: 'error' });
        }
    };

    const handleDeleteTask = async (taskId) => {
        if (!window.confirm('Вы уверены, что хотите удалить эту рассылку? Все логи также будут удалены.')) {
            return;
        }

        try {
            await axios.delete(`/broadcasts/${taskId}`);
            enqueueSnackbar('Рассылка удалена', { variant: 'success' });
            loadTasks();
            loadStats();
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || 'Ошибка удаления рассылки', { variant: 'error' });
        }
    };

    const handleRetryTask = async (taskId) => {
        try {
            await axios.post(`/broadcasts/${taskId}/retry`);
            enqueueSnackbar('Рассылка добавлена в очередь', { variant: 'success' });
            loadTasks();
            loadStats();
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || 'Ошибка повтора рассылки', { variant: 'error' });
        }
    };

    const handleForceStopTask = async (taskId) => {
        if (!window.confirm('Вы уверены, что хотите принудительно остановить эту рассылку? Она будет отменена и следующая рассылка начнет выполняться.')) {
            return;
        }

        try {
            await axios.patch(`/broadcasts/${taskId}/force-stop`);
            enqueueSnackbar('Рассылка принудительно остановлена', { variant: 'success' });
            loadTasks();
            loadStats();
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || 'Ошибка остановки рассылки', { variant: 'error' });
        }
    };

    const getStatusChip = (status) => {
        const statusConfig = {
            pending: { label: 'Ожидает', color: 'default', icon: <ScheduleIcon /> },
            in_progress: { label: 'Выполняется', color: 'primary', icon: <PlayArrowIcon /> },
            completed: { label: 'Завершена', color: 'success', icon: <CheckCircleIcon /> },
            failed: { label: 'Ошибка', color: 'error', icon: <ErrorIcon /> },
            cancelled: { label: 'Отменена', color: 'warning', icon: <CancelIcon /> }
        };

        const config = statusConfig[status] || statusConfig.pending;
        return <Chip label={config.label} color={config.color} size="small" icon={config.icon} />;
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return format(new Date(date), 'dd.MM.yyyy HH:mm', { locale: ru });
    };

    const calculateProgress = (task) => {
        if (task.progress.total === 0) return 0;
        return Math.round(((task.progress.sent + task.progress.failed + task.progress.skipped) / task.progress.total) * 100);
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" component="h1">
                    Рассылки
                </Typography>
                <Stack direction="row" spacing={2}>
                    <Button
                        variant="outlined"
                        startIcon={<HelpIcon />}
                        onClick={() => setGuideOpen(true)}
                        color="info"
                    >
                        Инструкция
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => { loadTasks(); loadStats(); }}
                    >
                        Обновить
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/broadcasts/create')}
                    >
                        Создать рассылку
                    </Button>
                </Stack>
            </Box>

            {stats && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <MessageIcon color="primary" sx={{ fontSize: 40 }} />
                                    <Box>
                                        <Typography variant="h4">{stats.tasks.total}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Всего рассылок
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <ScheduleIcon color="warning" sx={{ fontSize: 40 }} />
                                    <Box>
                                        <Typography variant="h4">{stats.tasks.pending}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            В очереди
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <SendIcon color="success" sx={{ fontSize: 40 }} />
                                    <Box>
                                        <Typography variant="h4">{stats.messages.sent.toLocaleString()}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Отправлено
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <ErrorIcon color="error" sx={{ fontSize: 40 }} />
                                    <Box>
                                        <Typography variant="h4">{stats.messages.failed.toLocaleString()}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Ошибок
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {stats?.queue?.isProcessing && stats.queue.currentTask && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        <strong>Выполняется:</strong> {stats.queue.currentTask.title}
                    </Typography>
                    <LinearProgress 
                        variant="determinate" 
                        value={calculateProgress(stats.queue.currentTask)} 
                        sx={{ mt: 1 }}
                    />
                    <Typography variant="caption">
                        {stats.queue.currentTask.progress.sent} / {stats.queue.currentTask.progress.total} отправлено
                    </Typography>
                </Alert>
            )}

            <Paper>
                <Tabs
                    value={statusFilter}
                    onChange={(e, newValue) => {
                        setStatusFilter(newValue);
                        setPage(0);
                    }}
                    sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                >
                    <Tab label="Все" value="all" />
                    <Tab label="Ожидают" value="pending" />
                    <Tab label="Выполняются" value="in_progress" />
                    <Tab label="Завершены" value="completed" />
                    <Tab label="Ошибки" value="failed" />
                    <Tab label="Отменены" value="cancelled" />
                </Tabs>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Название</TableCell>
                                <TableCell>Статус</TableCell>
                                <TableCell>Боты</TableCell>
                                <TableCell>Прогресс</TableCell>
                                <TableCell>Создано</TableCell>
                                <TableCell>Автор</TableCell>
                                <TableCell align="right">Действия</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : tasks.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            Нет рассылок
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tasks.map((task) => (
                                    <TableRow key={task._id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {task.title}
                                            </Typography>
                                            {task.recurring?.enabled && (
                                                <Chip 
                                                    label={`🔄 Каждые ${task.recurring.frequency} ${
                                                        task.recurring.interval === 'minutes' ? 'мин' :
                                                        task.recurring.interval === 'hours' ? 'ч' :
                                                        task.recurring.interval === 'days' ? 'д' :
                                                        task.recurring.interval === 'weeks' ? 'нед' : 'мес'
                                                    }`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            )}
                                            {task.scheduledAt && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Запланировано: {formatDate(task.scheduledAt)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{getStatusChip(task.status)}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5}>
                                                {task.bots.slice(0, 2).map((bot) => (
                                                    <Chip
                                                        key={bot._id}
                                                        label={`@${bot.username}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                                {task.bots.length > 2 && (
                                                    <Chip
                                                        label={`+${task.bots.length - 2}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ minWidth: 200 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption">
                                                        {task.progress.sent + task.progress.failed + task.progress.skipped} / {task.progress.total}
                                                    </Typography>
                                                    <Typography variant="caption">
                                                        {calculateProgress(task)}%
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={calculateProgress(task)}
                                                    sx={{ height: 6, borderRadius: 1 }}
                                                />
                                                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                                    <Chip label={`✓ ${task.progress.sent}`} size="small" color="success" />
                                                    <Chip label={`✗ ${task.progress.failed}`} size="small" color="error" />
                                                    <Chip label={`⊘ ${task.progress.skipped}`} size="small" />
                                                </Stack>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">
                                                {formatDate(task.createdAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">
                                                {task.createdBy?.username || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Tooltip title="Детали">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewDetails(task)}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                
                                                {(task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled' || task.status === 'in_progress') && (
                                                    <Tooltip title="Логи">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleViewLogs(task)}
                                                        >
                                                            <MessageIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {task.status === 'pending' && (
                                                    <Tooltip title="Отменить">
                                                        <IconButton
                                                            size="small"
                                                            color="warning"
                                                            onClick={() => handleCancelTask(task._id)}
                                                        >
                                                            <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {task.status === 'in_progress' && (
                                                    <Tooltip title="Принудительно остановить">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleForceStopTask(task._id)}
                                                        >
                                                            <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {task.status !== 'in_progress' && (
                                                    <Tooltip title="Редактировать">
                                                        <IconButton
                                                            size="small"
                                                            color="info"
                                                            onClick={() => navigate(`/broadcasts/edit/${task._id}`)}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {(task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled') && (
                                                    <Tooltip title="Повторить">
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            onClick={() => handleRetryTask(task._id)}
                                                        >
                                                            <RefreshIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {task.status !== 'in_progress' && (
                                                    <Tooltip title="Удалить">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteTask(task._id)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalTasks}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    labelRowsPerPage="Строк на странице:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
                />
            </Paper>

            <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Детали рассылки</DialogTitle>
                <DialogContent>
                    {selectedTask && (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Название</Typography>
                                <Typography variant="body1">{selectedTask.title}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Сообщение</Typography>
                                <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedTask.message}
                                    </Typography>
                                </Paper>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Боты</Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                    {selectedTask.bots.map((bot) => (
                                        <Chip key={bot._id} label={`@${bot.username}`} />
                                    ))}
                                </Stack>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Статус</Typography>
                                    {getStatusChip(selectedTask.status)}
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Приоритет</Typography>
                                    <Typography variant="body1">{selectedTask.priority}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Создано</Typography>
                                    <Typography variant="body2">{formatDate(selectedTask.createdAt)}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Автор</Typography>
                                    <Typography variant="body2">{selectedTask.createdBy?.username}</Typography>
                                </Grid>
                            </Grid>
                            {selectedTask.error && (
                                <Alert severity="error">
                                    <Typography variant="body2">{selectedTask.error}</Typography>
                                </Alert>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsOpen(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={logsOpen} onClose={() => setLogsOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Логи рассылки: {selectedTask?.title}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mb: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Поиск по Telegram ID или username..."
                                    value={logsSearch}
                                    onChange={(e) => setLogsSearch(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            loadLogs(selectedTask._id, logsStatusFilter, logsSearch);
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <Button 
                                                size="small" 
                                                onClick={() => loadLogs(selectedTask._id, logsStatusFilter, logsSearch)}
                                            >
                                                Найти
                                            </Button>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Stack direction="row" spacing={1}>
                                    <Chip
                                        label="Все"
                                        onClick={() => {
                                            setLogsStatusFilter('');
                                            loadLogs(selectedTask._id, '', logsSearch);
                                        }}
                                        color={logsStatusFilter === '' ? 'primary' : 'default'}
                                        variant={logsStatusFilter === '' ? 'filled' : 'outlined'}
                                    />
                                    <Chip
                                        label="✓ Отправлено"
                                        onClick={() => {
                                            setLogsStatusFilter('sent');
                                            loadLogs(selectedTask._id, 'sent', logsSearch);
                                        }}
                                        color={logsStatusFilter === 'sent' ? 'success' : 'default'}
                                        variant={logsStatusFilter === 'sent' ? 'filled' : 'outlined'}
                                    />
                                    <Chip
                                        label="✗ Ошибки"
                                        onClick={() => {
                                            setLogsStatusFilter('failed');
                                            loadLogs(selectedTask._id, 'failed', logsSearch);
                                        }}
                                        color={logsStatusFilter === 'failed' ? 'error' : 'default'}
                                        variant={logsStatusFilter === 'failed' ? 'filled' : 'outlined'}
                                    />
                                    <Chip
                                        label="⊘ Пропущено"
                                        onClick={() => {
                                            setLogsStatusFilter('skipped');
                                            loadLogs(selectedTask._id, 'skipped', logsSearch);
                                        }}
                                        color={logsStatusFilter === 'skipped' ? 'warning' : 'default'}
                                        variant={logsStatusFilter === 'skipped' ? 'filled' : 'outlined'}
                                    />
                                </Stack>
                            </Grid>
                        </Grid>
                    </Stack>

                    {logsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Stack spacing={3}>
                            {logsStats && (
                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h4" color="success.main">
                                                    {logsStats.sent}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Отправлено
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h4" color="error.main">
                                                    {logsStats.failed}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Ошибок
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h4" color="warning.main">
                                                    {logsStats.skipped}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Пропущено
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}

                            {errorSummary.length > 0 && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Сводка ошибок
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Причина</TableCell>
                                                    <TableCell align="right">Количество</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {errorSummary.map((error, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{error.description || 'Неизвестная ошибка'}</TableCell>
                                                        <TableCell align="right">
                                                            <Chip label={error.count} size="small" color="error" />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Детальные логи (последние 50)
                                </Typography>
                                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Пользователь</TableCell>
                                                <TableCell>Бот</TableCell>
                                                <TableCell>Статус</TableCell>
                                                <TableCell>Причина</TableCell>
                                                <TableCell>Время</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {logs.map((log) => (
                                                <TableRow key={log._id}>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {log.user.firstName} {log.user.lastName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            @{log.user.username || log.user.userId}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption">
                                                            @{log.bot?.username}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={log.status}
                                                            size="small"
                                                            color={
                                                                log.status === 'sent' ? 'success' :
                                                                log.status === 'failed' ? 'error' : 'default'
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption">
                                                            {log.error?.description || log.reason || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption">
                                                            {formatDate(log.sentAt)}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLogsOpen(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>

            <BroadcastGuide open={guideOpen} onClose={() => setGuideOpen(false)} />
        </Container>
    );
}
